import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Loader2, ArrowRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Reveal } from "@/components/Reveal";
import { COMPANY, INDUSTRY_OPTIONS } from "@/data/site";
import { createInquiry } from "@/lib/api";

const inputBase =
  "w-full bg-transparent border border-[#C9C3B4] text-[#0F172A] placeholder:text-[#9B9689] px-4 py-3.5 rounded-none focus:outline-none focus:border-[#0055FF] transition-colors duration-300 font-body text-sm";

const labelBase =
  "font-mono-tech text-[10px] uppercase tracking-widest text-[#55524B] mb-2 block";

const empty = { name: "", company: "", email: "", phone: "", industry: "", message: "" };

export const QuoteForm = ({ prefill }) => {
  const [form, setForm] = useState(empty);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [flash, setFlash] = useState(false);
  const messageRef = useRef(null);

  useEffect(() => {
    if (!prefill) return;
    setForm((f) => ({
      ...f,
      industry: prefill.industry || f.industry,
      message: prefill.message || f.message,
    }));
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1600);
    return () => clearTimeout(t);
  }, [prefill]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const message = date
        ? `${form.message}\n\nPreferred callback: ${format(date, "PPP")}`
        : form.message;
      await createInquiry({ ...form, message });
      toast.success("Request received. Our team will get back to you shortly.");
      setForm(empty);
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative bg-[#FFFFFF] py-24 md:py-40 border-t border-[#C9C3B4]"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Left copy + contact */}
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#0055FF]">
              / Request a quote
            </span>
            <h2 className="font-display font-800 tracking-tighter text-4xl md:text-6xl mt-6 leading-[0.9]">
              Let&apos;s mould <br /> something.
            </h2>
            <p className="text-[#55524B] mt-6 leading-relaxed max-w-md">
              Share your part, drawing, or idea. We&apos;ll come back with tooling,
              material and production options tailored to your run.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-12 space-y-6 border-t border-[#C9C3B4] pt-8">
            {[
              ["Email", COMPANY.email],
              ["Phone", COMPANY.phone],
              ["Address", COMPANY.address],
              ["Hours", COMPANY.hours],
            ].map(([k, v]) => (
              <div key={k} className="flex flex-col">
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#0055FF]">
                  {k}
                </span>
                <span className="text-[#0F172A] mt-1 text-sm md:text-base">{v}</span>
              </div>
            ))}
          </Reveal>
        </div>

        {/* Right form */}
        <div className="lg:col-span-7">
          <Reveal delay={0.05}>
            <form onSubmit={submit} data-testid="quote-form" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelBase}>Full Name *</label>
                  <input
                    data-testid="quote-name"
                    className={inputBase}
                    placeholder="Jane Doe"
                    value={form.name}
                    onChange={update("name")}
                  />
                </div>
                <div>
                  <label className={labelBase}>Company</label>
                  <input
                    data-testid="quote-company"
                    className={inputBase}
                    placeholder="Acme Pvt. Ltd."
                    value={form.company}
                    onChange={update("company")}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelBase}>Email *</label>
                  <input
                    data-testid="quote-email"
                    type="email"
                    className={inputBase}
                    placeholder="jane@acme.com"
                    value={form.email}
                    onChange={update("email")}
                  />
                </div>
                <div>
                  <label className={labelBase}>Phone</label>
                  <input
                    data-testid="quote-phone"
                    className={inputBase}
                    placeholder="+91 00000 00000"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelBase}>Industry</label>
                  <Select
                    value={form.industry}
                    onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}
                  >
                    <SelectTrigger
                      data-testid="quote-industry-trigger"
                      className="w-full bg-transparent border border-[#C9C3B4] text-[#0F172A] rounded-none px-4 py-6 focus:ring-0 focus:border-[#0055FF] font-body text-sm"
                    >
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-[#C9C3B4] text-[#0F172A] rounded-none">
                      {INDUSTRY_OPTIONS.map((opt) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          data-testid={`industry-opt-${opt}`}
                          className="font-body text-sm focus:bg-[#EBE7DE] focus:text-[#0F172A] cursor-pointer"
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className={labelBase}>Preferred Callback</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="quote-date-trigger"
                        className={`${inputBase} flex items-center justify-between text-left ${date ? "text-[#0F172A]" : "text-[#9B9689]"}`}
                      >
                        {date ? format(date, "PPP") : "Pick a date"}
                        <CalendarIcon className="h-4 w-4 text-[#0055FF]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#FFFFFF] border-[#C9C3B4] rounded-none" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div>
                <label className={labelBase}>Project Details *</label>
                <textarea
                  ref={messageRef}
                  data-testid="quote-message"
                  rows={5}
                  className={`${inputBase} resize-none ${flash ? "border-[#0055FF] ring-1 ring-[#0055FF]/40" : ""}`}
                  placeholder="Tell us about the part, volumes, material and timeline..."
                  value={form.message}
                  onChange={update("message")}
                />
              </div>

              <button
                type="submit"
                data-testid="quote-submit"
                disabled={loading}
                className="btn-industrial group w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#0055FF] hover:bg-[#3377FF] disabled:opacity-60 text-white px-10 py-4 font-mono-tech text-xs uppercase tracking-widest"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Submit Request
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
